import { useContext } from 'react';
import {
  EDUCATIONAL_PERIOD_PREFERENCE,
  PREFERENCE_CONTEXT_SUBJECT,
} from 'legacy-src/constants/types';

import { PREAP_SUBJECTS } from 'chameleon-src/constants/subjects';
import { Context, TYPES } from 'chameleon/ui-stack/utilities/app-loader';

import { getTeacherInfo } from 'legacy-src/queries';

import { getDefaultEducationPeriod } from 'legacy-src/actions/educationPeriod';
import {
  getSubjectEducationPeriods,
  findChosenEducationPeriod,
} from 'legacy-src/components/auth/utils';

const useAcademicYearSelection = ({
  client,
  setDefaultEducationPeriodCode,
  userData,
  setUserSubjects,
  setItems,
  savePreference,
  setIsLoading,
  preApChameleonFlag,
  subject,
  APAYDropdownAtTop,
  subjectEducationPeriods,
  chosenEducationPeriod,
}) => {
  const { me, isTeacher } = userData;
  const [, chameleonDispatch] = useContext(Context);

  const showAcademicYearSelector =
    APAYDropdownAtTop &&
    subjectEducationPeriods.length > 1 &&
    chosenEducationPeriod &&
    isTeacher;

  const onPeriodSelect = (educationPeriodId) => {
    setIsLoading(true);
    const isPreApSubject = PREAP_SUBJECTS.includes(parseInt(subject, 10));

    setDefaultEducationPeriodCode({
      educationPeriodCode: educationPeriodId,
      userData,
    });

    client
      .query({
        name: 'newTeacherInfoData',
        query: getTeacherInfo,
        variables: {
          id: subject,
          educationPeriod: educationPeriodId,
        },
      })
      .then((response) => {
        const {
          teacherSubject: { classes, subjects },
        } = response.data;
        setItems(classes);
        setUserSubjects({ subjects });
        savePreference({
          variables: {
            userId: me.initId,
            preference: EDUCATIONAL_PERIOD_PREFERENCE,
            value: educationPeriodId,
            context: PREFERENCE_CONTEXT_SUBJECT,
            contextId: subjects.find(
              (s) => parseInt(s.masterSubjectId, 10) === parseInt(subject, 10),
            )?.initId,
          },
        }).then(() => {
          setIsLoading(false);
          chameleonDispatch({
            type: TYPES.SET_USE_CHAMELEON_BASED_ON_EP,
            payload: {
              isPreApSubject,
              preApChameleonFlag,
              epCode: educationPeriodId,
            },
          });
        });
      });
  };

  const onSubjectClick = (event, subjectId) => {
    // no need to call graphQL if we don't have the FF enabled or user is not a teacher
    const isPreApSubject = PREAP_SUBJECTS.includes(parseInt(subjectId, 10));

    // We used to have AppWrapper component which was handling Legacy/Chameleon switching
    // logic, it did a "refresh" of the render app inside the App.js
    // coincidentally, some components in units-outlines relied on it and are
    // missing dependency on `subjectId` is their useEffect or other hooks.

    // Finding at retesting all the components does not sounds feasible at this
    // point, so I decided to re-render them with this trick.
    setIsLoading(true);
    requestAnimationFrame(() => {
      setIsLoading(false);
    });

    if (!isTeacher || !APAYDropdownAtTop || !isPreApSubject) {
      return;
    }

    const { educationPeriodCode } = getDefaultEducationPeriod({
      subject: subjectId,
      userData,
    });

    const subjectEPs = getSubjectEducationPeriods({
      subjectId,
      teacherSubjects: userData.teacherSubjects,
    });

    const educationPeriod = findChosenEducationPeriod({
      currentEducationPeriod: userData.educationPeriod.currentEducationPeriod,
      subjectEducationPeriods: subjectEPs,
      me: userData.me,
      userSubjects: userData.teacherSubjects,
      subject: subjectId,
    });

    setDefaultEducationPeriodCode({
      subjectId,
      educationPeriodCode: educationPeriod.id,
      userData,
    });

    chameleonDispatch({
      type: TYPES.SET_USE_CHAMELEON_BASED_ON_EP,
      payload: {
        isPreApSubject,
        preApChameleonFlag,
        epCode: educationPeriodCode.id,
      },
    });
  };

  return {
    onPeriodSelect,
    onSubjectClick,
    showAcademicYearSelector,
  };
};

export default useAcademicYearSelection;
